/** ***********
*
* <group-families-list.component.ts v1.0>
*
* Copyright (c) 2022 AIRBUS OPERATIONS
*
* Contributors:
*  GARY Adrien (NG635E8) [adrien.gary.external@airbus.com]
*  NG6C740 [thomas.pereira.external@airbus.com]
*
* All rights reserved.
**************/

import { SelectionModel } from '@angular/cdk/collections';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { combineLatest, distinctUntilChanged, map, Observable, Subscription, tap } from 'rxjs';
import { Family, FamilyRight } from 'src/app/models/family';
import { GroupFamily, RoleLevel, RoleLevelEnum } from 'src/app/models/user-right';
import { FamilySelected, SelectFamilyComponent } from '../select-family/select-family.component';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { getLevelSortingMagnitude } from '../../../_shared/sort-helper';
import { MatPaginator } from '@angular/material/paginator';
import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { Store } from '@ngrx/store';
import { selectMyRightsTree$ } from 'src/app/store/selectors/user-right.selectors';
import { isEqual } from 'lodash';

export type GroupFamilyListItem = (GroupFamily & { parentFamilyName?: string; });
@Component({
    selector: 'app-group-families-list',
    templateUrl: './group-families-list.component.html',
    styleUrls: ['../group-deps-list.component.scss', './group-families-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class GroupFamiliesListComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {

    @ViewChild('table') public tableContainer: ElementRef<HTMLDivElement>;
    @ViewChild(MatButton) public addFamilyButton: MatButton;
    @ViewChild(MatSort, { static: true }) sort: MatSort;
    @ViewChild(MatPaginator, { static: false }) paginator: MatPaginator;

    @Input() public families: GroupFamilyListItem[] = [];    // #region familiesLoading
    private _familiesLoading = false;
    @Input()
    public set familiesLoading(value: BooleanInput) {
        this._familiesLoading = coerceBooleanProperty(value);
    }
    public get familiesLoading(): boolean {
        return this._familiesLoading;
    }
    // #endregion
    @Input() public height = 'auto';
    // #region disabled
    private _disabled = false;
    @Input()
    public set disabled(value: BooleanInput) {
        this._disabled = coerceBooleanProperty(value);
    }
    public get disabled(): boolean {
        return this._disabled;
    }
    // #endregion
    // #region isOwner
    private _isOwner = false;
    @Input()
    public set isOwner(value: BooleanInput) {
        this._isOwner = coerceBooleanProperty(value);
    }
    public get isOwner(): boolean {
        return this._isOwner;
    }
    // #endregion
    @Output() removeFamilies = new EventEmitter<Family['id'][]>();
    @Output() updateFamilyRole = new EventEmitter<GroupFamily>();
    @Output() addFamilies = new EventEmitter<GroupFamily[]>();
    @Output() pageChange = new EventEmitter<void>();

    public selection = new SelectionModel<Family>(true);
    public dataSource = new MatTableDataSource<GroupFamilyListItem>();
    public baseDisplayedColumns: string[] = ['parentFamilyName', 'name', 'level'];
    public availableRolesForSelection = [
        RoleLevelEnum.reader,
        RoleLevelEnum.operator,
        RoleLevelEnum.manager,
    ];
    public availableRolesForSelectionByFamily: Record<Family['id'], typeof this.availableRolesForSelection> = {}

    private _subscription = new Subscription();

    get displayedColumns(): string[] {
        return this.isOwner ? [...this.baseDisplayedColumns, 'actions'] : this.baseDisplayedColumns;
    }

    private userRights$: Observable<FamilyRight[]> = this.store.select(selectMyRightsTree$).pipe(
        map(parentFamilyRights => parentFamilyRights.map(
            parentFamilyRight => parentFamilyRight.families
        ).flat()),
        distinctUntilChanged(isEqual),
    );

    constructor(
        private _cd: ChangeDetectorRef,
        private _dialog: MatDialog,
        private store: Store,
    ) { }

    ngOnInit(): void {
        this._subscription.add(
            combineLatest([
                this.dataSource.connect(),
                this.userRights$,
            ]).subscribe(
                ([data, rights]) => {
                    this.availableRolesForSelectionByFamily = data.reduce(
                        (rolesByFamily, family) => {
                            rolesByFamily[family.id] = this._getAvailableRights(rights.find(
                                ({ id }) => id === family.id
                            ));
                            return rolesByFamily;
                        },
                        {}
                    );
                }
            )
        );
    }

ngOnChanges(changes: SimpleChanges): void {
    if (
        changes.familiesLoading &&
        changes.familiesLoading.currentValue !==
            changes.familiesLoading.previousValue
    ) {
        this._cd.markForCheck();
    }

    if (
        changes.families &&
        changes.families.currentValue !==
            changes.families.previousValue
    ) {
        const updatedFamilies =
            changes.families.currentValue as GroupFamilyListItem[] ?? [];
        const validFamilies = updatedFamilies.filter(
            family =>
                typeof family.parentFamilyName === 'string' &&
                family.parentFamilyName.trim().length > 0
        );

        const remainingFamilyIds = new Set(
            validFamilies.map(family => family.id)
        );

        [...this.selection.selected]
            .filter(
             selectedFamily =>
                  !remainingFamilyIds.has(selectedFamily.id)
            )
            .forEach(
                selectedFamily =>
                    this.selection.deselect(selectedFamily)
            );

        this.dataSource.data = [...validFamilies];
        this._cd.markForCheck();
    }
}

    ngAfterViewInit(): void {
        this.dataSource.sort = this.sort;
        this.dataSource.paginator = this.paginator;
        this.dataSource.sortingDataAccessor = (data, sortHeaderId) => {
            switch (sortHeaderId) {
                case 'level':
                    return getLevelSortingMagnitude(data.level);
                default:
                    return data[sortHeaderId];
            }
        };
    }

    /**
     * Get the available levels that can be selected
     */
    private _getAvailableRights(right?: FamilyRight): RoleLevel[] {
        switch (right?.level) {
            case RoleLevelEnum.reader:
                return [RoleLevelEnum.reader];
            case RoleLevelEnum.operator:
                return [RoleLevelEnum.reader, RoleLevelEnum.operator];
            case RoleLevelEnum.manager:
                return [RoleLevelEnum.reader, RoleLevelEnum.operator, RoleLevelEnum.manager];
            default:
                return [RoleLevelEnum.basic];
        }
    }

    /**
     * Open the family selection dialog & emit `addFamilies` event with the selected ones on close
     */
    public onClickAdd(): void {
        const dialog = this._dialog.open(SelectFamilyComponent, {
            width: '400px',
            maxWidth: '90vw',
            minWidth: '25vw',
            data: this.families,
        });
        this.addFamilyButton?._elementRef.nativeElement.classList.remove('highlight');
        this._subscription.add(
            dialog.afterClosed().subscribe(
                (families: FamilySelected[]) => {
                    // If cancelled, the families is undefined
                    if (families) {
                        this.addFamilies.emit(families.map(
                            ({ id, scope, name }) => ({
                                id,
                                name,
                                scope,
                                level: RoleLevelEnum.reader,
                            })
                        ));
                    }
                }
            )
        );
    }

    /**
     * Emits the `removeFamilies` event with the clicked family
     * @param families The families removed
     */
    public onClickRemove(families: Family & {removed?:boolean}): void {
        this.removeFamilies.emit([families.id]);
    }

    /**
     * Emits the `removeFamilies` event with the selected families
     */
    public onClickRemoveMass(): void {
        const selectedIds = this.selection.selected.map(({ id }) => id);

        if (!selectedIds.length) {
            return;
        }

        this.selection.clear();
        this._cd.markForCheck();

        this.removeFamilies.emit(selectedIds);
    }

    /**
     * Emits the `updateFamilyRole` event with the family and its level
     */
    public onSelectedRoleChange(family: GroupFamily, selectedRole: RoleLevel): void {
        this.updateFamilyRole.emit({
            ...family,
            level: selectedRole as RoleLevelEnum,
        });
    }

    /**
       * Called when the user change the paginator page
       * - Aims to auto scroll to the top of the table
       */
    public scrollUp() {
        const elements = this.tableContainer.nativeElement.children;
        let table: HTMLTableElement;
        for (let i = 0; i < elements.length; i++) {
            if (elements.item(i).tagName === 'TABLE') {
                table = elements.item(i) as HTMLTableElement;
                break;
            }
        }
        table?.scrollIntoView({ behavior: 'smooth' });
        table?.scrollTo(0, 0);
        this.pageChange.emit();
    }

    ngOnDestroy(): void {
        this._subscription.unsubscribe();
    }

}
